<template>
  <v-container fluid>
    <v-row class="mb-2">
      <v-col xs="12" md="12" sm="12">
        <v-card>
          <v-card-title>Голосовые ассистенты</v-card-title>

          <v-form ref="form" lazy-validation v-model="formRules.valid">
            <v-card-text>
              <v-row>
                <v-col xs="12" sm="6" md="4" lg="4" xl="4">
                  <TimeInterval v-model="date" />
                </v-col>

                <v-col xs="12" sm="6" md="4" lg="4" xl="4" align="right">
                  <!-- todo вынести селектор Таргет АИ агентов в отдельный компонент -->
                  <v-autocomplete
                    v-model="targetAiAgent"
                    label="Агент"
                    :items="targetAiAgents"
                    :rules="[v => !!v || 'Данное поле обязательно']"
                  />
                </v-col>


                <v-col xs="12" sm="12" md="4" lg="4" align="right">
                  <v-btn
                    color="success"
                    class="mt-3"
                    @click="loadReport"
                  >Поиск</v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-form>
        </v-card>
      </v-col>
    </v-row>

    <v-expansion-panels v-model="totalStatExpansion" class="mb-4" hover>
      <v-expansion-panel
        :disabled="totalStatLoading && !hasTotalStatBadges"
        class="mb-2"
      >
        <v-expansion-panel-header style="position: relative;">
          <v-row v-if="hasTotalStatBadges" align="center" class="flex-grow-1" no-gutters>
            <v-col cols="auto" class="pr-2">
              <v-tooltip right max-width="400">
                <template #activator="{ on }">
                  <v-icon v-on="on" color="info">mdi-information-slab-symbol</v-icon>
                </template>
                <div class="text-body-2">
                  Сводка за выбранный период и агента. Детализация звонков в таблице ниже.
                </div>
              </v-tooltip>
            </v-col>
            <v-col cols="auto" class="mr-7">
              <v-badge bordered color="success" overlap>
                <template #badge>
                  <span>{{ totalStat.total_calls }}</span>
                </template>
                <v-tooltip bottom max-width="360">
                  <template #activator="{ on }">
                    <v-chip color="success" label outlined v-on="on">Всего принятых</v-chip>
                  </template>
                  <span>Общее число звонков, связанных с агентом, за период</span>
                </v-tooltip>
              </v-badge>
            </v-col>
            <v-col cols="auto" class="mr-7">
              <v-badge bordered color="success" overlap>
                <template #badge>
                  <span>{{ formatMinutesNum(totalStat.total_spoken_minutes) }}</span>
                </template>
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-chip color="success" label outlined v-on="on">Минут (поминутная тарификация)</v-chip>
                  </template>
                  <span>Суммарное время, когда агент говорил (по звонкам, где агент был активен)</span>
                </v-tooltip>
              </v-badge>
            </v-col>
          </v-row>
          <span v-else>Общая статистика</span>
          <v-progress-linear
            v-if="totalStatLoading"
            indeterminate
            color="green"
            style="position: absolute; bottom: 2px; left: 0; width: 100%;"
            class="ma-0"
          />
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <v-alert
            v-if="totalStatLoadFailed"
            type="warning"
            dense
            text
            class="mb-2"
          >
            Не удалось загрузить сводку. Повторите поиск.
          </v-alert>

          <v-simple-table
            v-else-if="hasTotalStatTable"
            dense
          >
            <tbody>
              <tr>
                <th>Всего принятых</th>
                <td>{{ totalStat.total_calls }}</td>
              </tr>
              <tr>
                <th>
                  <v-tooltip bottom max-width="400">
                    <template #activator="{ on }">
                      <span v-on="on" style="cursor: help;">Минут (поминутная тарификация)</span>
                    </template>
                    <span>Суммарная длительность, когда агент говорил</span>
                  </v-tooltip>
                </th>
                <td>{{ formatMinutesText(totalStat.total_spoken_minutes) }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-alert
            v-else-if="totalStatSearched && !totalStatLoading"
            type="info"
            dense
            text
            class="mb-0"
          >
            Сводка за выбранные условия пуста.
          </v-alert>

          <p v-else class="text-body-2 grey--text mb-0">
            Сводка появится после нажатия «Поиск».
          </p>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card class="mt-5">
      <v-data-table
        :headers="report.headers"
        :items="report.items"
        :items-per-page="report.items.length"
        :loading="loading"
        loading-text="Загрузка..."
        no-data-text="Ничего не найдено"
        hide-default-footer
      >
        <template #[`item.actions`]="{ item }">
          <v-btn icon @click="openCallModal(item)">
            <v-icon>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="callModal.open" max-width="700" @click:outside="closeCallModal">
      <v-card v-if="callModal.item" class="d-flex flex-column call-modal-card">
        <v-card-title class="d-flex align-center justify-space-between flex-shrink-0">
          <span>Звонок — {{ callModal.item.phoneNumbers }}</span>
          <v-btn icon @click="closeCallModal"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-progress-linear v-if="callModal.loading" indeterminate color="primary" class="flex-shrink-0" />

        <v-card-text v-if="!callModal.loading" ref="transcriptScroll" class="flex-grow-1 overflow-y-auto">
          <!-- Стенограмма -->
          <div class="subtitle-2 mb-2">Стенограмма</div>
          <div
            v-if="callModal.item.transcript && callModal.item.transcript.length"
            class="transcript"
          >
            <div
              v-for="(line, idx) in callModal.item.transcript"
              :key="idx"
              :ref="'tline-' + idx"
              class="transcript-line mb-2"
              :class="[
                line.role === 'assistant' ? 'transcript-operator' : 'transcript-client',
                activeTranscriptIdx === idx ? 'transcript-active' : ''
              ]"
            >
              <span class="transcript-role font-weight-bold mr-2">
                {{ line.role === 'assistant' ? 'Ассистент' : 'Клиент' }}:
              </span>
              <span class="transcript-time grey--text mr-2">{{ formatTime(line.start_time / 1000) }}</span>
              <span>{{ line.content }}</span>
            </div>
          </div>

          <v-alert v-else type="info" dense text>
            Стенограмма недоступна
          </v-alert>
        </v-card-text>

        <!-- Аудиоплеер -->
        <div class="flex-shrink-0">
          <v-divider />
          <div class="pa-3">
            <AudioPlayerExternal
              v-if="callModal.item.audioLoaded"
              ref="audio-player"
              :blob="callModal.item.audioBlob"
              :current-time.sync="callModal.item.currentTime"
              :fileName="callModal.item.audioFileName"
            />
            <v-alert v-else-if="!callModal.item.audioLoaded" type="info" dense text class="mb-0">
              Аудиозапись загружается
            </v-alert>
            <v-alert v-else type="info" dense text class="mb-0">
              Аудиозапись недоступна
            </v-alert>
          </div>
        </div>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<style>

</style>

<script>
import moment from 'moment'

import { mapActions, mapGetters } from 'vuex'
import { requiredRule } from '~/plugins/rules'
import TimeInterval from './elements/TimeInterval.vue'
import AudioPlayerExternal from '~/components/partials/AudioPlayerExternal.vue'

export default {
  name: 'TargetAi',
  components: {
    TimeInterval,
    AudioPlayerExternal,
  },

  data() {
    return {
      totalStatExpansion: null,
      totalStat: null,
      totalStatLoading: false,
      totalStatLoadFailed: false,
      totalStatSearched: false,
      reportMode: 'groupByProjects',
      formRules: {
        valid: false,
        required: requiredRule,
      },
      report: {
        headers: [
          {text: 'id', value: 'interaction_id'},
          {text: 'Дата и Время звонка', value: 'call_date_time'},
          {text: 'Номер', value: 'client_phone'},
          {text: 'Продолжительность звонка, посек', value: 'duration_seconds'},
          {text: 'Продолжительность звонка, помин', value: 'duration_minutes'},
          {text: 'Просмотр звонка', value: 'actions'},
        ],
        items: [],
      },

      date: {},
      targetAiAgent: null,
      targetAiAgents: [],
      callModal: {
        open: false,
        loading: false,
        item: {
          audioLoaded: false,
          audioURL: null,
          transcript: [],
          phoneNumbers: '',
          audioBlob: null,
          currentTime: 0,
          audioFileName: '',
        },
      },
    }
  },

  computed: {
    ...mapGetters({
      loading: 'shared/getLoading',
    }),

    hasTotalStatTable() {
      return this.totalStat != null
    },

    hasTotalStatBadges() {
      return this.hasTotalStatTable
    },

    activeTranscriptIdx() {
      const transcript = this.callModal.item.transcript
      if (!transcript || !transcript.length) return -1
      const currentTime = this.callModal.item.currentTime
      let idx = -1
      for (let i = 0; i < transcript.length; i++) {
        if (transcript[i].start_time / 1000 <= currentTime) idx = i
        else break
      }
      return idx
    },
  },

  watch: {
    activeTranscriptIdx(idx) {
      if (idx < 0) return
      this.$nextTick(() => {
        const el = this.$refs[`tline-${idx}`]
        const el0 = Array.isArray(el) ? el[0] : el
        const container = this.$refs.transcriptScroll
        if (!el0 || !container) return
        const elTop = el0.offsetTop
        const elBottom = elTop + el0.offsetHeight
        const cTop = container.scrollTop
        const cBottom = cTop + container.clientHeight
        if (elTop < cTop || elBottom > cBottom) {
          container.scrollTo({ top: elTop - 70, behavior: 'smooth' })
        }
      })
    },
  },

  methods: {
    ...mapActions({
      loadTargetAiStatistic: 'target_ai/loadTargetAiStatistic',
      loadTargetAiTotalStat: 'target_ai/loadTargetAiTotalStat',
      loadTargetAiAgent: 'target_ai/loadTargetAiAgent',
      loadAudio: 'target_ai/loadAudio',
      loadTranscribe: 'target_ai/loadTranscribe',
      resetAllState: 'resetAllState',
    }),

    async openCallModal(item) {
      this.callModal.item.audioLoaded = false
      this.callModal.item.currentTime = 0
      this.callModal.item.phoneNumbers = item.client_phone
      this.callModal.item.audioFileName = item.client_phone + '_' + item.call_date_time
      this.callModal.open = true
      this.callModal.loading = true

      this.loadAudio(item.interaction_id).then((data) => {
        const audioBlob = new Blob([data], { type: 'audio/mpeg' })
        const audioUrl = URL.createObjectURL(audioBlob);

        this.callModal.item.audioBlob = audioBlob
        this.callModal.item.audioURL = audioUrl
        this.callModal.item.audioLoaded = true

        this.callModal.loading = false
      })
      this.loadTranscribe(item.interaction_id).then((data) => {
        this.callModal.item.transcript = data

        this.callModal.loading = false
      })
    },

    closeCallModal() {
      this.callModal.open = false
      this.callModal.loading = false
      this.callModal.item.audioLoaded = false
      this.callModal.item.audioURL = ''
      this.callModal.item.transcript = []
    },

    formatTime(secs) {
      let minutes = Math.floor(secs / 60) || 0
      let seconds = Math.floor(secs - minutes * 60) || 0
      if (seconds < 10) seconds = `0${seconds}`

      return `${minutes}:${seconds}`
    },

    formatMinutesNum(n) {
      if (n == null || n === '' || Number.isNaN(Number(n))) return '—'
      return String(Number(n))
    },

    formatMinutesText(n) {
      return this.formatMinutesNum(n)
    },

    async loadReport() {
      if (!this.$refs.form.validate()) return

      const payload = {
        period: [
          moment(this.date.start).format('YYYY-MM-DD HH:mm:ss'),
          moment(this.date.end).format('YYYY-MM-DD HH:mm:ss')
        ],
        agent_id: this.targetAiAgent
      }

      this.totalStatLoading = true
      this.totalStatLoadFailed = false
      this.totalStat = null
      try {
        const [items, totalRes] = await Promise.all([
          this.loadTargetAiStatistic(payload),
          this.loadTargetAiTotalStat(payload)
        ])
        this.report.items = items || []
        this.totalStat = totalRes.row
        this.totalStatLoadFailed = totalRes.requestFailed
        this.totalStatSearched = true
      } finally {
        this.totalStatLoading = false
      }
    },

  },

  async mounted() {
    const rawAgents = await this.loadTargetAiAgent()
    this.targetAiAgents = rawAgents.map(agent => ({text: agent.name, value: agent.id}))
  },

  beforeDestroy() {
    this.resetAllState()
  },
}
</script>

<style scoped lang="scss">
.call-modal-card {
  max-height: 80vh;
}
.transcript-line {
  padding: 6px 10px;
  border-radius: 4px;

  .theme--light & {
    background: #f5f5f5;
  }
  .theme--dark & {
    background: #f5f5f526;
  }

  &.transcript-active {
    background: #1976d214;
    outline: 1px solid #1976d240;
  }
}
.transcript-operator {
  border-right: 3px solid #1976d2;
}
.transcript-client {
  border-left: 3px solid #43a047;
}
.transcript-role {
  font-size: 0.85rem;
}
.transcript-time {
  font-size: 0.75rem;
}

</style>
